"use client";

import type { InputHTMLAttributes } from "react";
import cn from "@/utils/cn";

export interface QuantifierProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  id: string;
  dir?: "row" | "column" | "block";
  className?: string;
  max?: number;
  min?: number;
  label?: string;
  value: number;
  onChange: (value: number) => void;
}

/**
 * @name Quantifier
 * @description Renders a quantity input with increment/decrement buttons.
 *
 * @param {QuantifierProps} props - Component props.
 * @param {string} props.id - The unique identifier for the quantifier.
 * @param {'row' | 'column' | 'block'} [props.dir='row'] - The layout direction of the quantifier controls.
 * @param {string} [props.className] - Additional CSS classes.
 * @param {number} [props.max] - The maximum allowed value.
 * @param {number} [props.min=1] - The minimum allowed value.
 * @param {string} [props.label] - Optional label for the quantifier.
 * @param {number} props.value - The current value.
 * @param {(value: number) => void} props.onChange - Callback function when the value changes.
 * @returns {JSX.Element} The rendered quantifier component.
 */
const Quantifier = ({
  id,
  dir = "row",
  className,
  max,
  min = 1,
  label,
  value,
  onChange,
  ...props
}: QuantifierProps) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    onChange(newValue);
  };

  const handleIncrement = () => {
    if (max === undefined || value < max) {
      onChange(value + 1);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  return (
    <div
      className={cn(
        className,
        dir === "block"
          ? "flex flex-col items-center gap-4"
          : `flex items-center gap-4 ${dir === "row" ? "flex-row" : "flex-col"}`
      )}
    >
      {dir === "block" ? (
        <>
          <input
            type="number"
            name={id}
            id={id}
            className="border rounded-lg max-w-18 text-center text-lg font-medium"
            value={value}
            onChange={handleInputChange}
            {...props}
          />
          <div className="flex items-center w-full">
            <button
              type="button"
              id="qty-btn-minus"
              className="border rounded-lg w-[50%] h-6 flex items-center justify-center"
              onClick={handleDecrement}
              disabled={value <= min}
            >
              -
            </button>
            <button
              type="button"
              id="qty-btn-plus"
              className="border rounded-lg w-[50%] h-6 flex items-center justify-center"
              onClick={handleIncrement}
              disabled={max !== undefined && value >= max}
            >
              +
            </button>
          </div>
          {label && <label htmlFor={id}>{label}</label>}
        </>
      ) : (
        <>
          <button
            type="button"
            id="qty-btn-minus"
            className="btn-rounded"
            onClick={handleDecrement}
            disabled={value <= min}
          >
            -
          </button>
          <input
            type="number"
            name={id}
            id={id}
            className="input max-w-18 text-center text-lg font-medium"
            value={value}
            onChange={handleInputChange}
            {...props}
          />
          <button
            type="button"
            id="qty-btn-plus"
            className="btn-rounded"
            onClick={handleIncrement}
            disabled={max !== undefined && value >= max}
          >
            +
          </button>
        </>
      )}
    </div>
  );
};

export default Quantifier;
